import React, { useEffect, useRef } from 'react';
import {
  PortExtendedInnerInner,
  PolygonGeometry,
} from '@ais-ui/api-spec/portserver';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { Button, Label, ScrollArea } from '@ais-ui/ui-core';
import { PiPlus } from 'react-icons/pi';
import { UseFormReturn } from 'react-hook-form';
import { InnerAreaItem } from './InnerAreaItem';
import { InnerAreaForm } from './InnerAreaForm';
import { InnerAreaFormValues, convertPolygonToForm } from './constants';

interface InnerAreasListProps {
  innerAreas: PortExtendedInnerInner[];
  outerPolygon?: PolygonGeometry;
  onChange: (areas: PortExtendedInnerInner[]) => void;
  /** Function to control parent dialog visibility during drawing */
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** Index of inner area being edited (null = none) */
  editingIndex: number | null;
  setEditingIndex: React.Dispatch<React.SetStateAction<number | null>>;
  /** Whether add form is shown */
  isAdding: boolean;
  setIsAdding: React.Dispatch<React.SetStateAction<boolean>>;
  /** Form for inner area editing - lives at parent level to persist */
  innerAreaForm: UseFormReturn<InnerAreaFormValues>;
  /** Start drawing function from parent */
  onStartDrawingInnerArea: (
    type: 'Polygon' | 'Point',
    existingShape: GeoJson | null,
  ) => void;
}

export const InnerAreasList = ({
  innerAreas,
  outerPolygon,
  onChange,
  setShowDialog,
  editingIndex,
  setEditingIndex,
  isAdding,
  setIsAdding,
  innerAreaForm,
  onStartDrawingInnerArea,
}: InnerAreasListProps) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isAdding) {
      // Wait for form to render, then scroll to bottom
      requestAnimationFrame(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    }
  }, [isAdding]);

  const handleEdit = (index: number) => {
    const area = innerAreas[index];
    // Reset form with the area's values
    innerAreaForm.reset({
      name: area.name || '',
      terminalType: area.terminalType || 'Undefined',
      polygon: convertPolygonToForm(area.polygon),
    });
    setIsAdding(false);
    setEditingIndex(index);
  };

  const handleDelete = (index: number) => {
    const newAreas = [...innerAreas];
    newAreas.splice(index, 1);
    onChange(newAreas);
  };

  const handleAddClick = () => {
    // Reset form for new area
    innerAreaForm.reset({
      name: '',
      terminalType: 'Undefined',
      polygon: null,
    });
    setEditingIndex(null);
    setIsAdding(true);
  };

  const handleSaveNew = (values: InnerAreaFormValues) => {
    if (!values.polygon) return;

    const newArea: PortExtendedInnerInner = {
      name: values.name,
      terminalType: values.terminalType,
      polygon: {
        type: 'Polygon',
        coordinates: values.polygon.coordinates as number[][][],
      },
    };

    onChange([...innerAreas, newArea]);
    setIsAdding(false);
  };

  const handleSaveEdit = (index: number, values: InnerAreaFormValues) => {
    if (!values.polygon) return;

    const newAreas = [...innerAreas];
    newAreas[index] = {
      ...newAreas[index],
      name: values.name,
      terminalType: values.terminalType,
      polygon: {
        type: 'Polygon',
        coordinates: values.polygon.coordinates as number[][][],
      },
    };

    onChange(newAreas);
    setEditingIndex(null);
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setIsAdding(false);
  };

  // Get other inner areas for reference (exclude the one being edited)
  const getOtherAreas = (excludeIndex?: number) => {
    return innerAreas
      .map((area, idx) => ({ id: area.id, polygon: area.polygon, idx }))
      .filter((_, idx) => idx !== excludeIndex);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Inner Areas</Label>
        {!isAdding && editingIndex === null && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddClick}
            className="h-7 text-xs"
          >
            <PiPlus className="size-3 mr-1" />
            Add Area
          </Button>
        )}
      </div>

      <ScrollArea className="h-[340px] w-full overflow-hidden">
        <div className="space-y-2 pr-3">
          {/* Existing inner areas */}
          {innerAreas.map((area, index) => (
            <React.Fragment key={area.id || index}>
              {editingIndex === index ? (
                <InnerAreaForm
                  outerPolygon={outerPolygon}
                  otherInnerAreas={getOtherAreas(index)}
                  onSave={(values) => handleSaveEdit(index, values)}
                  onCancel={handleCancel}
                  form={innerAreaForm}
                  onStartDrawing={onStartDrawingInnerArea}
                  isEditing
                />
              ) : (
                <InnerAreaItem
                  area={area}
                  index={index}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              )}
            </React.Fragment>
          ))}

          {/* Add new form */}
          {isAdding && (
            <InnerAreaForm
              outerPolygon={outerPolygon}
              otherInnerAreas={getOtherAreas()}
              onSave={handleSaveNew}
              onCancel={handleCancel}
              form={innerAreaForm}
              onStartDrawing={onStartDrawingInnerArea}
            />
          )}

          {/* Empty state */}
          {innerAreas.length === 0 && !isAdding && (
            <p className="text-sm text-muted-foreground py-2">
              No inner areas defined. Click "Add Area" to create one.
            </p>
          )}
          <div ref={bottomRef} />
        </div>
      </ScrollArea>
    </div>
  );
};
